// src/pages/AuthPage.jsx - Firebase Trigger Email Extension ile E-posta Doğrulama
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Mail, Lock, User, Eye, EyeOff, AlertCircle,
  CheckCircle, ArrowLeft, Key, RefreshCw
} from 'lucide-react';
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  createUserWithEmailAndPassword,
  updateProfile
} from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import toast from 'react-hot-toast';
import pnglogo from '../assets/tronalogo.png';
import Header from '../components/Header';
import { useSearch } from '../context/SearchContext';
import { openCartSidebar } from '../components/CartSidebar';
import { getAllProducts } from '../services/productService';
import { getAllCampaigns } from '../services/discountService';
import Footer from '../components/Footer';

// E-posta doğrulama servisi
import {
  createVerificationCode,
  verifyCode,
  resendVerificationCode
} from '../services/emailVerificationService';

// Doğrulama kodu süresi (dakika)
const VERIFICATION_CODE_EXPIRY = 10;

export default function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';

  // Context hooks
  const { refreshCart, cartCount } = useCart();
  const { refreshWishlist, wishlistCount } = useWishlist();
  const { searchQuery, setSearchQuery } = useSearch();

  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [availableCategories, setAvailableCategories] = useState([]);
  
  // E-posta doğrulama state'leri
  const [verificationStep, setVerificationStep] = useState('none'); // 'none', 'pending', 'success'
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verificationTimer, setVerificationTimer] = useState(VERIFICATION_CODE_EXPIRY * 60);
  const [verificationResendEnabled, setVerificationResendEnabled] = useState(false);
  const [verificationToken, setVerificationToken] = useState('');

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({});

  // Doğrulama zamanlayıcısı
  useEffect(() => {
    let timer;
    if (verificationStep === 'pending' && verificationTimer > 0) {
      timer = setInterval(() => {
        setVerificationTimer(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            setVerificationResendEnabled(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [verificationStep, verificationTimer]);

  // Ürün ve kampanya yükleme
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsResult, campaignsResult] = await Promise.all([
          getAllProducts(),
          getAllCampaigns(),
        ]);

        if (productsResult.success) {
          const activeProducts = productsResult.products.filter(product =>
            product.isActive !== false
          );
          setProducts(activeProducts);

          const categoriesSet = new Set();
          activeProducts.forEach(product => {
            if (product.category) {
              const mainCat = product.category.includes(' > ')
                ? product.category.split(' > ')[0]
                : product.category;
              categoriesSet.add(mainCat);
            }
          });
          setAvailableCategories(Array.from(categoriesSet));
        }

        if (campaignsResult.success) {
          const now = new Date();
          const activeCampaigns = campaignsResult.campaigns.filter(campaign => {
            if (!campaign.endDate) return false;
            const endDate = new Date(campaign.endDate);
            return endDate > now;
          });
          setCampaigns(activeCampaigns);
        }

      } catch (error) {
        console.error('Veri yükleme hatası:', error);
        toast.error('Bağlantı hatası');
      }
    };
    fetchData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!isLogin && !formData.name.trim()) {
      newErrors.name = 'Ad Soyad zorunludur';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'E-posta zorunludur';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Geçerli bir e-posta girin';
    }

    if (!forgotPassword && verificationStep === 'none') {
      if (!formData.password) {
        newErrors.password = 'Şifre zorunludur';
      } else if (formData.password.length < 8) {
        newErrors.password = 'Şifre en az 8 karakter olmalıdır';
      }

      if (!isLogin && formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Şifreler eşleşmiyor';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Doğrulama kodu input yönetimi
  const handleVerificationCodeChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Sadece rakam
    if (value.length > 1) return;
    
    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);

    // Otomatik focus
    if (value && index < 5) {
      const nextInput = document.getElementById(`verification-code-${index + 1}`);
      if (nextInput) nextInput.focus();
    }

    // Tüm inputlar dolu mu?
    if (newCode.every(char => char) && index === 5) {
      setTimeout(() => handleVerifyCode(newCode.join('')), 300);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      const prevInput = document.getElementById(`verification-code-${index - 1}`);
      if (prevInput) {
        prevInput.focus();
        const newCode = [...verificationCode];
        newCode[index - 1] = '';
        setVerificationCode(newCode);
      }
    }
  };

  // E-posta doğrulama kodu gönder
  const sendVerificationEmail = async () => {
    setLoading(true);
    try {
      const result = await createVerificationCode(
        formData.email,
        formData.name,
        formData.password
      );

      if (result.success) {
        setVerificationToken(result.token);
        setVerificationEmail(formData.email);
        setVerificationStep('pending');
        setVerificationTimer(VERIFICATION_CODE_EXPIRY * 60);
        setVerificationResendEnabled(false);
        toast.success(`6 haneli doğrulama kodu ${formData.email} adresine gönderildi.`);
      } else {
        throw new Error(result.message || 'Kod gönderilemedi');
      }
    } catch (error) {
      console.error('Doğrulama e-postası hatası:', error);
      toast.error(error.message || 'Doğrulama kodu gönderilemedi. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  // Doğrulama kodunu yeniden gönder
  const resendCode = async () => {
    if (!verificationResendEnabled || loading) return;
    
    setLoading(true);
    try {
      const result = await resendVerificationCode(verificationToken);

      if (result.success) {
        setVerificationTimer(VERIFICATION_CODE_EXPIRY * 60);
        setVerificationResendEnabled(false);
        setVerificationCode(['', '', '', '', '', '']);
        toast.success('Yeni doğrulama kodu gönderildi.');
      } else {
        throw new Error(result.message || 'Kod gönderilemedi');
      }
    } catch (error) {
      toast.error(error.message || 'Kod gönderilemedi. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  // Doğrulama kodunu kontrol et ve kullanıcı oluştur
  const handleVerifyCode = async (code = null) => {
    const verifyCodeValue = code || verificationCode.join('');
    
    if (verifyCodeValue.length !== 6) {
      toast.error('Lütfen 6 haneli kodu girin.');
      return;
    }

    setLoading(true);
    try {
      // Backend'de kodu doğrula
      const result = await verifyCode(verificationToken, verifyCodeValue);

      if (!result.success) {
        if (result.expired) {
          setVerificationResendEnabled(true);
        }
        throw new Error(result.message || 'Doğrulama başarısız');
      }

      // Kod doğru, şimdi Firebase Auth'da kullanıcı oluştur
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        result.email,
        result.password
      );

      // Profili güncelle
      await updateProfile(userCredential.user, {
        displayName: result.name
      });

      // Firestore'a kullanıcı bilgilerini kaydet
      const isAdmin = result.email === 'info@tronapazarlama.com';
      
      await setDoc(doc(db, 'Users', userCredential.user.uid), {
        uid: userCredential.user.uid,
        email: result.email,
        displayName: result.name,
        role: isAdmin ? 'admin' : 'user',
        emailVerified: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isActive: true,
        photoURL: null,
        phoneNumber: null,
        addresses: [],
        orders: [],
        wishlist: [],
        cart: []
      });

      // localStorage'a kaydet
      localStorage.setItem('isLoggedIn', 'true');
      localStorage.setItem('userToken', userCredential.user.uid);
      localStorage.setItem('userName', result.name);
      localStorage.setItem('userEmail', result.email);
      localStorage.setItem('userId', userCredential.user.uid);
      localStorage.setItem('emailVerified', 'true');

      // Context'leri yenile
      await Promise.all([
        refreshCart(),
        refreshWishlist()
      ]);

      setVerificationStep('success');
      toast.success('✓ E-posta doğrulandı! Kayıt işleminiz tamamlandı.');
      
      // 2 saniye sonra yönlendir
      setTimeout(() => {
        if (isAdmin) {
          navigate('/admin');
        } else {
          navigate(from, { replace: true });
        }
      }, 2000);

    } catch (error) {
      console.error('Doğrulama hatası:', error);
      
      if (error.message.includes('expired') || error.message.includes('doldu')) {
        toast.error('Doğrulama kodunun süresi doldu. Lütfen yeni kod isteyin.');
        setVerificationResendEnabled(true);
      } else if (error.message.includes('invalid') || error.message.includes('Geçersiz')) {
        toast.error(error.message);
      } else if (error.code === 'auth/email-already-in-use') {
        toast.error('Bu e-posta adresi zaten kullanımda.');
        setVerificationStep('none');
        resetForm();
      } else {
        toast.error(error.message || 'Doğrulama başarısız. Lütfen tekrar deneyin.');
      }
      
      // Kodu temizle
      setVerificationCode(['', '', '', '', '', '']);
      const firstInput = document.getElementById('verification-code-0');
      if (firstInput) firstInput.focus();
      
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      // Firestore'dan kullanıcı bilgilerini al
      const userDocRef = doc(db, 'Users', userCredential.user.uid);
      const userDocSnap = await getDoc(userDocRef);

      // Kullanıcı yoksa oluştur
      if (!userDocSnap.exists()) {
        const isAdmin = formData.email === 'info@tronapazarlama.com';
        await setDoc(userDocRef, {
          uid: userCredential.user.uid,
          email: userCredential.user.email,
          displayName: userCredential.user.displayName || 'Kullanıcı',
          role: isAdmin ? 'admin' : 'user',
          emailVerified: userCredential.user.emailVerified,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          isActive: true,
          photoURL: userCredential.user.photoURL || null,
          phoneNumber: userCredential.user.phoneNumber || null,
          addresses: [],
          orders: [],
          wishlist: [],
          cart: []
        });
      }

      const userData = userDocSnap.exists() ? userDocSnap.data() : null;
      const isAdmin = userData?.role === 'admin' || formData.email === 'info@tronapazarlama.com';

      // localStorage'a kaydet
      localStorage.setItem('isLoggedIn', 'true');
      localStorage.setItem('userToken', userCredential.user.uid);
      localStorage.setItem('userName', userCredential.user.displayName || userCredential.user.email.split('@')[0]);
      localStorage.setItem('userEmail', userCredential.user.email);
      localStorage.setItem('userId', userCredential.user.uid);
      localStorage.setItem('emailVerified', userCredential.user.emailVerified.toString());

      // Context'leri yenile
      await Promise.all([
        refreshCart(),
        refreshWishlist()
      ]);

      toast.success('Giriş başarılı!');

      // Yönlendirme
      if (isAdmin) {
        navigate('/admin');
      } else {
        navigate(from, { replace: true });
      }
    } catch (error) {
      console.error('Login error:', error);
      if (error.code === 'auth/user-not-found') {
        toast.error('Kullanıcı bulunamadı');
      } else if (error.code === 'auth/wrong-password') {
        toast.error('Hatalı şifre');
      } else if (error.code === 'auth/invalid-credential') {
        toast.error('Geçersiz e-posta veya şifre');
      } else {
        toast.error('Giriş başarısız');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    // E-posta doğrulama kodunu gönder
    await sendVerificationEmail();
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!formData.email.trim()) {
      setErrors({ email: 'E-posta adresi gerekli' });
      return;
    }

    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, formData.email);
      toast.success('Şifre sıfırlama bağlantısı e-postanıza gönderildi');
      setForgotPassword(false);
    } catch (error) {
      console.error('Password reset error:', error);
      toast.error('E-posta gönderilemedi');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      confirmPassword: ''
    });
    setErrors({});
    setVerificationStep('none');
    setVerificationCode(['', '', '', '', '', '']);
    setVerificationToken('');
  };

  const switchMode = () => {
    setIsLogin(!isLogin);
    setForgotPassword(false);
    resetForm();
  };

  const toggleForgotPassword = () => {
    setForgotPassword(!forgotPassword);
    resetForm();
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  return (
    <div className="auth-page">
      <Style />

      <Header
        wishlistCount={wishlistCount}
        cartCount={cartCount}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onSearch={handleSearch}
        onCartClick={openCartSidebar}
        onWishlistClick={() => navigate('/wishlist')}
        onCampaignsClick={() => navigate('/campaigns')}
        availableCategories={availableCategories}
        products={products}
      />

      <div className="auth-container">
        <div className="auth-grid">
          {/* Left Side - Branding */}
          <div className="auth-branding">
            <div className="branding-content">
              <div className="auth-logo" onClick={() => navigate('/')}>
                <img src={pnglogo} alt="Trona" />
              </div>
              <h1>Trona'ya Hoş Geldiniz</h1>
              <p>Güvenli alışverişin adresi. Binlerce ürün, hızlı teslimat ve güvenilir hizmet.</p>

              <div className="features-list">
                <div className="feature-item">
                  <CheckCircle size={20} />
                  <span>Ücretsiz kargo fırsatları</span>
                </div>
                <div className="feature-item">
                  <CheckCircle size={20} />
                  <span>Güvenli ödeme altyapısı</span>
                </div>
                <div className="feature-item">
                  <CheckCircle size={20} />
                  <span>7/24 müşteri desteği</span>
                </div>
                <div className="feature-item">
                  <CheckCircle size={20} />
                  <span>Özel kampanyalar ve indirimler</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="auth-form-container">
            <div className="auth-form-wrapper">
              {/* Doğrulama başlığı */}
              {verificationStep === 'pending' ? (
                <div className="verification-header">
                  <div className="verification-icon">
                    <Key size={48} />
                  </div>
                  <h2>E-posta Doğrulama</h2>
                  <p>
                    <strong>{verificationEmail}</strong> adresine 6 haneli doğrulama kodu gönderdik.
                    Lütfen e-postanızı kontrol edin.
                  </p>
                  <div className="verification-timer">
                    <span>Kodun geçerlilik süresi: </span>
                    <strong className={verificationTimer < 60 ? 'text-red' : ''}>
                      {formatTime(verificationTimer)}
                    </strong>
                  </div>
                </div>
              ) : verificationStep === 'success' ? (
                <div className="verification-header success">
                  <div className="verification-icon success">
                    <CheckCircle size={48} />
                  </div>
                  <h2>Kayıt Başarılı!</h2>
                  <p>E-posta adresiniz doğrulandı. Ana sayfaya yönlendiriliyorsunuz...</p>
                </div>
              ) : (
                <div className="form-header">
                  <h2>
                    {forgotPassword
                      ? 'Şifremi Unuttum'
                      : isLogin
                        ? 'Giriş Yap'
                        : 'Kayıt Ol'}
                  </h2>
                  <p>
                    {forgotPassword
                      ? 'E-posta adresinize şifre sıfırlama bağlantısı göndereceğiz'
                      : isLogin
                        ? 'Hesabınıza giriş yapın'
                        : 'Yeni hesap oluşturun'}
                  </p>
                </div>
              )}

              {/* Doğrulama kodu girme ekranı */}
              {verificationStep === 'pending' && (
                <div className="verification-container">
                  <div className="verification-code-inputs">
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                      <input
                        key={index}
                        id={`verification-code-${index}`}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength="1"
                        value={verificationCode[index]}
                        onChange={(e) => handleVerificationCodeChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        className="verification-code-input"
                        autoFocus={index === 0}
                        disabled={loading}
                      />
                    ))}
                  </div>

                  <div className="verification-actions">
                    <button
                      type="button"
                      className="submit-btn"
                      onClick={() => handleVerifyCode()}
                      disabled={loading || verificationCode.some(char => !char)}
                    >
                      {loading ? (
                        <div className="spinner-small"></div>
                      ) : (
                        'Doğrula ve Kaydı Tamamla'
                      )}
                    </button>

                    <div className="resend-section">
                      <p>Kodu almadınız mı?</p>
                      <button
                        type="button"
                        className={`resend-btn ${!verificationResendEnabled ? 'disabled' : ''}`}
                        onClick={resendCode}
                        disabled={!verificationResendEnabled || loading}
                      >
                        <RefreshCw size={16} />
                        {verificationResendEnabled ? 'Yeni Kod Gönder' : `Yeni kod (${formatTime(verificationTimer)})`}
                      </button>
                    </div>

                    <button
                      type="button"
                      className="back-btn"
                      onClick={resetForm}
                      disabled={loading}
                    >
                      <ArrowLeft size={16} />
                      Geri Dön
                    </button>
                  </div>

                  <div className="verification-note">
                    <AlertCircle size={14} />
                    <span>Spam/gereksiz klasörünüzü kontrol etmeyi unutmayın.</span>
                  </div>
                </div>
              )}

              {/* Normal form ekranı */}
              {verificationStep === 'none' && (
                <form onSubmit={forgotPassword ? handleForgotPassword : isLogin ? handleLogin : handleRegister}>
                  {/* Name Field */}
                  {!isLogin && !forgotPassword && (
                    <div className="form-group">
                      <label htmlFor="name">Ad Soyad</label>
                      <div className="input-wrapper">
                        <User className="input-icon" size={20} />
                        <input
                          type="text"
                          id="name"
                          name="name"
                          placeholder="Ad ve soyadınızı girin"
                          value={formData.name}
                          onChange={handleInputChange}
                          className={errors.name ? 'error' : ''}
                          disabled={loading}
                        />
                      </div>
                      {errors.name && (
                        <div className="error-message">
                          <AlertCircle size={14} />
                          <span>{errors.name}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Email Field */}
                  <div className="form-group">
                    <label htmlFor="email">E-posta</label>
                    <div className="input-wrapper">
                      <Mail className="input-icon" size={20} />
                      <input
                        type="email"
                        id="email"
                        name="email"
                        placeholder="ornek@email.com"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={errors.email ? 'error' : ''}
                        disabled={loading}
                      />
                    </div>
                    {errors.email && (
                      <div className="error-message">
                        <AlertCircle size={14} />
                        <span>{errors.email}</span>
                      </div>
                    )}
                  </div>

                  {/* Password Field */}
                  {!forgotPassword && (
                    <div className="form-group">
                      <label htmlFor="password">Şifre</label>
                      <div className="input-wrapper">
                        <Lock className="input-icon" size={20} />
                        <input
                          type={showPassword ? 'text' : 'password'}
                          id="password"
                          name="password"
                          placeholder="En az 8 karakter"
                          value={formData.password}
                          onChange={handleInputChange}
                          className={errors.password ? 'error' : ''}
                          disabled={loading}
                        />
                        <button
                          type="button"
                          className="toggle-password"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label={showPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
                        >
                          {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                      </div>
                      {errors.password && (
                        <div className="error-message">
                          <AlertCircle size={14} />
                          <span>{errors.password}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Confirm Password */}
                  {!isLogin && !forgotPassword && (
                    <div className="form-group">
                      <label htmlFor="confirmPassword">Şifre Tekrar</label>
                      <div className="input-wrapper">
                        <Lock className="input-icon" size={20} />
                        <input
                          type={showPassword ? 'text' : 'password'}
                          id="confirmPassword"
                          name="confirmPassword"
                          placeholder="Şifrenizi tekrar girin"
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          className={errors.confirmPassword ? 'error' : ''}
                          disabled={loading}
                        />
                      </div>
                      {errors.confirmPassword && (
                        <div className="error-message">
                          <AlertCircle size={14} />
                          <span>{errors.confirmPassword}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Forgot Password Link */}
                  {isLogin && !forgotPassword && (
                    <div className="form-footer-link">
                      <button
                        type="button"
                        className="text-btn"
                        onClick={toggleForgotPassword}
                      >
                        Şifremi unuttum
                      </button>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    className="submit-btn"
                    disabled={loading}
                  >
                    {loading ? (
                      <div className="spinner-small"></div>
                    ) : forgotPassword ? (
                      'Sıfırlama Bağlantısı Gönder'
                    ) : isLogin ? (
                      'Giriş Yap'
                    ) : (
                      'Kayıt Ol ve Doğrulama Kodu Gönder'
                    )}
                  </button>

                  {/* Switch Mode */}
                  <div className="switch-mode">
                    {forgotPassword ? (
                      <p>
                        <button
                          type="button"
                          className="text-btn"
                          onClick={toggleForgotPassword}
                        >
                          Giriş sayfasına dön
                        </button>
                      </p>
                    ) : (
                      <p>
                        {isLogin ? 'Hesabınız yok mu?' : 'Zaten hesabınız var mı?'}
                        <button
                          type="button"
                          className="text-btn primary"
                          onClick={switchMode}
                        >
                          {isLogin ? 'Kayıt Ol' : 'Giriş Yap'}
                        </button>
                      </p>
                    )}
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Style() {
  return (
    <style>{`
      * { 
        margin: 0; 
        padding: 0; 
        box-sizing: border-box; 
        -webkit-tap-highlight-color: transparent;
      }
      
      .auth-page {
        min-height: 100vh;
        background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
      }

      .auth-container {
        max-width: 1320px;
        margin: 0 auto;
        padding: 60px 20px;
      }

      .auth-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 60px;
        align-items: center;
      }

      .auth-branding {
        padding: 40px;
      }

      .branding-content {
        max-width: 500px;
      }

      .auth-logo {
        cursor: pointer;
        display: inline-block;
        transition: transform 0.3s ease;
      }

      .auth-logo:hover {
        transform: scale(1.05);
      }

      .auth-logo img {
        height: 100px;
        width: auto;
        display: block;
      }

      .branding-content h1 {
        font-size: 42px;
        font-weight: 800;
        margin-bottom: 16px;
        margin-top: 24px;
        color: #2B2B2B;
        line-height: 1.2;
      }

      .branding-content > p {
        font-size: 18px;
        color: #495057;
        margin-bottom: 40px;
        line-height: 1.6;
      }

      .features-list {
        display: flex;
        flex-direction: column;
        gap: 16px;
      }

      .feature-item {
        display: flex;
        align-items: center;
        gap: 12px;
        font-size: 16px;
        color: #495057;
      }

      .feature-item svg {
        color: #06d6a0;
        flex-shrink: 0;
      }

      .auth-form-container {
        background: #fff;
        border-radius: 24px;
        box-shadow: 0 8px 32px rgba(0,0,0,0.08);
        padding: 48px;
      }

      .auth-form-wrapper {
        max-width: 440px;
        margin: 0 auto;
      }

      .form-header {
        margin-bottom: 32px;
        text-align: center;
      }

      .form-header h2 {
        font-size: 32px;
        font-weight: 800;
        margin-bottom: 8px;
        color: #2B2B2B;
      }

      .form-header p {
        font-size: 14px;
        color: #868e96;
        line-height: 1.5;
      }

      /* Verification Styles */
      .verification-header {
        text-align: center;
        margin-bottom: 32px;
      }

      .verification-header.success {
        padding: 20px 0;
      }

      .verification-icon {
        width: 80px;
        height: 80px;
        margin: 0 auto 20px;
        background: linear-gradient(135deg, #E5332A 0%, #ff6b6b 100%);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        animation: pulse 2s ease-in-out infinite;
      }

      .verification-icon.success {
        background: linear-gradient(135deg, #06d6a0 0%, #00b894 100%);
      }

      @keyframes pulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.05); }
      }

      .verification-header h2 {
        font-size: 28px;
        font-weight: 800;
        margin-bottom: 12px;
        color: #2B2B2B;
      }

      .verification-header p {
        font-size: 14px;
        color: #868e96;
        line-height: 1.6;
        margin-bottom: 16px;
      }

      .verification-header p strong {
        color: #E5332A;
        font-weight: 600;
      }

      .verification-timer {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 8px 16px;
        background: #f8f9fa;
        border-radius: 20px;
        font-size: 13px;
        color: #495057;
      }

      .verification-timer strong {
        color: #06d6a0;
        font-weight: 700;
      }

      .verification-timer strong.text-red {
        color: #dc3545;
        animation: blink 1s ease-in-out infinite;
      }

      @keyframes blink {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }

      .verification-container {
        display: flex;
        flex-direction: column;
        gap: 24px;
      }

      .verification-code-inputs {
        display: flex;
        gap: 12px;
        justify-content: center;
      }

      .verification-code-input {
        width: 56px;
        height: 64px;
        text-align: center;
        font-size: 24px;
        font-weight: 700;
        border: 2px solid #e9ecef;
        border-radius: 12px;
        transition: all 0.2s;
        outline: none;
        font-family: 'Courier New', monospace;
      }

      .verification-code-input:focus {
        border-color: #E5332A;
        background: #fff;
        box-shadow: 0 0 0 4px rgba(229, 51, 42, 0.1);
        transform: scale(1.05);
      }

      .verification-code-input:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      .verification-actions {
        display: flex;
        flex-direction: column;
        gap: 16px;
      }

      .resend-section {
        text-align: center;
        padding: 16px 0;
        border-top: 1px solid #e9ecef;
      }

      .resend-section p {
        font-size: 13px;
        color: #868e96;
        margin-bottom: 8px;
      }

      .resend-btn {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 8px 16px;
        background: #f8f9fa;
        border: 1px solid #dee2e6;
        border-radius: 8px;
        font-size: 13px;
        font-weight: 600;
        color: #495057;
        cursor: pointer;
        transition: all 0.2s;
      }

      .resend-btn:hover:not(.disabled) {
        background: #e9ecef;
        border-color: #ced4da;
      }

      .resend-btn.disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .back-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        padding: 12px;
        background: transparent;
        border: 1px solid #dee2e6;
        border-radius: 12px;
        font-size: 14px;
        font-weight: 600;
        color: #495057;
        cursor: pointer;
        transition: all 0.2s;
      }

      .back-btn:hover {
        background: #f8f9fa;
        border-color: #ced4da;
      }

      .verification-note {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 12px;
        background: #fff3cd;
        border: 1px solid #ffeaa7;
        border-radius: 8px;
        font-size: 12px;
        color: #856404;
      }

      .verification-note svg {
        flex-shrink: 0;
      }

      /* Form Styles */
      .form-group {
        margin-bottom: 24px;
      }

      .form-group label {
        display: block;
        font-size: 14px;
        font-weight: 600;
        color: #2B2B2B;
        margin-bottom: 8px;
      }

      .input-wrapper {
        position: relative;
      }

      .input-wrapper input {
        width: 100%;
        padding: 14px 16px 14px 48px;
        border: 2px solid #e9ecef;
        border-radius: 12px;
        font-size: 15px;
        transition: all 0.2s;
        outline: none;
        font-family: inherit;
      }

      .input-wrapper input:focus {
        border-color: #E5332A;
        background: #fff;
      }

      .input-wrapper input.error {
        border-color: #dc3545;
      }

      .input-wrapper input:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        background: #f8f9fa;
      }

      .input-icon {
        position: absolute;
        left: 16px;
        top: 50%;
        transform: translateY(-50%);
        color: #868e96;
        pointer-events: none;
      }

      .toggle-password {
        position: absolute;
        right: 16px;
        top: 50%;
        transform: translateY(-50%);
        background: none;
        border: none;
        color: #868e96;
        cursor: pointer;
        padding: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s;
        border-radius: 6px;
      }

      .toggle-password:hover {
        color: #495057;
        background: #f8f9fa;
      }

      .error-message {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-top: 6px;
        font-size: 13px;
        color: #dc3545;
      }

      .form-footer-link {
        text-align: right;
        margin-bottom: 24px;
      }

      .submit-btn {
        width: 100%;
        padding: 16px;
        background: #E5332A;
        color: #fff;
        border: none;
        border-radius: 12px;
        font-size: 16px;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
      }

      .submit-btn:hover:not(:disabled) {
        background: #c72a22;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(229, 51, 42, 0.3);
      }

      .submit-btn:active {
        transform: translateY(0);
      }

      .submit-btn:disabled {
        opacity: 0.7;
        cursor: not-allowed;
        transform: none;
      }

      .spinner-small {
        width: 20px;
        height: 20px;
        border: 3px solid rgba(255,255,255,0.3);
        border-top-color: #fff;
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
      }

      @keyframes spin {
        to { transform: rotate(360deg); }
      }

      .text-btn {
        background: none;
        border: none;
        color: #868e96;
        font-size: 14px;
        cursor: pointer;
        text-decoration: none;
        transition: color 0.2s;
        padding: 4px 8px;
        border-radius: 6px;
      }

      .text-btn:hover {
        color: #495057;
        background: #f8f9fa;
      }

      .text-btn.primary {
        color: #E5332A;
        font-weight: 600;
        margin-left: 6px;
      }

      .text-btn.primary:hover {
        color: #c72a22;
        background: #fff5f5;
      }

      .switch-mode {
        text-align: center;
        margin-top: 24px;
        padding-top: 24px;
        border-top: 1px solid #e9ecef;
      }

      .switch-mode p {
        font-size: 14px;
        color: #868e96;
      }

      /* Responsive */
      @media (max-width: 992px) {
        .auth-grid {
          grid-template-columns: 1fr;
          gap: 40px;
        }
        
        .auth-branding {
          order: 2;
          text-align: center;
        }
        
        .auth-form-container {
          order: 1;
        }

        .branding-content {
          margin: 0 auto;
        }
      }

      @media (max-width: 768px) {
        .auth-container {
          padding: 30px 16px;
        }

        .auth-form-container {
          padding: 32px 24px;
        }

        .verification-code-input {
          width: 48px;
          height: 56px;
          font-size: 20px;
        }

        .verification-code-inputs {
          gap: 8px;
        }
      }

      @media (max-width: 576px) {
        .auth-form-container {
          padding: 24px 16px;
        }

        .verification-icon {
          width: 64px;
          height: 64px;
        }

        .verification-icon svg {
          width: 32px;
          height: 32px;
        }

        .verification-code-input {
          width: 42px;
          height: 50px;
          font-size: 18px;
        }

        .verification-code-inputs {
          gap: 6px;
        }
      }
    `}</style>
  );
}